// src/ui/ChatInfoPane.jsx — the web ChatInfo, phase 1.
//
// Opens from the chat header, slides into the same squeeze slot the tools panel
// uses, and can be DRAGGED WIDER from its left edge — never narrower than its
// starting width (double-click the edge to snap back).
//
// Looks: the app's ChatInfoScreen "bubble-glow" — a neutral panel base that
// melts into the SENDER'S OWN bubble colour toward the bottom (the pane sits
// inside the chat root, so `--bubble-out` is already the per-chat themed value).
// The profile picture sits top-centre, and beneath it one horizontal strip
// holds Media / Links / Docs. Tapping media opens the fullscreen viewer (sender
// + time top-centre, download built in); tapping a doc opens the doc viewer
// with the same chrome.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SupporterBadge from './SupporterBadge';   // 💛 the Supporter badge, next to the name
import Avatar from './Avatar';
/* 🖼 tapping the picture shows the picture — see services/avatarPages.js for why
   a group with no photo is a LIST of pages rather than one empty one */
import AvatarViewer from './AvatarViewer';
import { avatarPages } from '../services/avatarPages';
import { activeMembers } from '../services/groupMembers';
import { getUserProfile } from '../services/users';
import { X, Play, Doc, Download, Link as LinkIcon, Lock, BellRing, ShieldLock, Alert, Verified, SecureCheck, Check, CheckCheck, Eye, Group as GroupIcon, Trash, Logout, Slash, Timer, Phone, Video } from './icons';
import { QRCodeSVG } from 'qrcode.react';
import GroupSettings from './GroupSettings';
import NewGroupModal from './NewGroupModal';
import AddToListModal from './AddToListModal';
import ExportChatModal from './ExportChatModal';
import ClearChatSheet from './ClearChatSheet';
import { mediaClearedMs, kindOf } from '../services/chatClear';
import { leaveGroup } from '../services/groupAdmin';
import { blockUser, unblockUser } from '../services/blocking';
import { watchTranslation, saveTranslation, LANGUAGES } from '../services/translate';
import { isOfficialChat } from '../services/support';
import { guestChatExpired } from '../services/guestLinks';
import { joinGroupCall } from '../services/groupCalls';
import {
  watchReadReceipts, setChatReadReceipts, clearChatReadReceipts, OFF_UNKNOWN,
} from '../services/readReceipts';
import { setChatLastSeenShared, setChatOnlineShared } from '../services/presenceVisibility';
import { onSnapshot, doc as fsDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  subscribe as pmSubscribe, isPrivateMediaOn, isSyncedOn, isLocalOn, isChatRevealed,
  setSynced as pmSetSynced, setLocal as pmSetLocal, setChatRevealed, watchPrivateMedia,
} from '../services/privateMedia';
import { docStyle, humanBytes } from '../services/documents';
import { normalizeLink } from '../services/links';
import { fullTimeLabel } from './format';
import { tsMs, isNoteToSelf } from '../services/chats';
import { loadAboutFor } from '../services/about';
import {
  chatNotifySettings, saveChatNotifySettings, MESSAGE_TONES, RINGTONES,
  playMessageTone, previewRingtone, requestNotifyPermission,
} from '../services/notify';
import {
  securityCodeFor, watchE2ee, setBaseline, markVerified, clearVerified, verdictOf,
} from '../services/e2eeVerify';
import { albumVisibleIndices } from '../services/mediaRuns'; // 🧩 an album's items one by one

const ListIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.width || 16} height={p.height || 16} fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 6h11M9 12h11M9 18h11" />
    <circle cx="4.5" cy="6" r="1.4" /><circle cx="4.5" cy="12" r="1.4" /><circle cx="4.5" cy="18" r="1.4" />
  </svg>
);

const Translate = (p) => (
  <svg viewBox="0 0 24 24" width={p.width || 14} height={p.height || 14} fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 5h10" /><path d="M9 3v2c0 4.4-2.2 7.5-5 9" />
    <path d="M6 12c1.6 2.3 3.9 4 6 5" />
    <path d="m12 20 4.5-10 4.5 10" /><path d="M14 17h5" />
  </svg>
);

const dataUri = (b64) => (b64 ? `data:image/jpeg;base64,${b64}` : null);
const msOf = (m) => tsMs(m?.timestamp) || 0;

/* ── the document viewer: mv-style chrome, sender + time dead centre ──────── */
function DocViewer({ m, senderName, mine, onClose }) {
  const name = m.documentName || 'Document';
  const { ext, color } = docStyle(name);
  const ready = !!m._media;
  const isPdf = /pdf/i.test(ext || '');
  const isImg = /^(png|jpe?g|gif|webp)$/i.test(ext || '');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="mv dv">
      <div className="mv-scrim" onClick={onClose} />
      <div className="mv-bar mv-top">
        <div className="mv-side mv-left" />
        <div className="mv-who">
          <div className="mv-who-name">{senderName || 'Allo user'}</div>
          <div className="mv-who-time">{mine ? 'Sent' : 'Received'} · {fullTimeLabel(msOf(m))}</div>
        </div>
        <div className="mv-side mv-right">
          {ready && (
            <a className="mv-btn" title="Download" href={m._media} download={name}>
              <Download />
            </a>
          )}
          <button className="mv-btn mv-close" title="Close (Esc)" onClick={onClose}><X width={22} height={22} /></button>
        </div>
      </div>
      <div className="mv-stage dv-stage" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        {!ready ? (
          <div className="dv-card">
            <span className="doc-tile lg" style={{ background: color }}>
              <Doc width={26} height={26} /><span className="doc-ext">{ext || 'FILE'}</span>
            </span>
            <b>{name}</b>
            <i><Lock width={13} height={13} /> Decrypting…</i>
          </div>
        ) : isPdf ? (
          <iframe className="dv-frame" title={name} src={m._media} />
        ) : isImg ? (
          <img className="mv-media" src={m._media} alt="" />
        ) : (
          <div className="dv-card">
            <span className="doc-tile lg" style={{ background: color }}>
              <Doc width={26} height={26} /><span className="doc-ext">{ext || 'FILE'}</span>
            </span>
            <b>{name}</b>
            <i>{[ext, humanBytes(m.documentSize)].filter(Boolean).join(' · ')}</i>
            <a className="lg-go slim" href={m._media} download={name}><Download width={15} height={15} /> Download</a>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── notifications: THIS BROWSER ONLY ────────────────────────────────────────
   Deliberately unsynced — the phone keeps its own per-chat notification
   settings and nothing here touches them (localStorage, `allo_np_<chatId>`).
   Vibes notifications are account-wide and stay out of this section. */
function Row({ label, sub, on, onFlip }) {
  return (
    <div className="ap-toggle-row">
      <div>{label}{sub ? <i>{sub}</i> : null}</div>
      <button className={`ap-switch ${on ? 'on' : ''}`} onClick={onFlip} role="switch" aria-checked={on} aria-label={label} />
    </div>
  );
}

function NotifySection({ chatId, isGroup, official, guest }) {
  const [s, setS] = useState(() => chatNotifySettings(chatId));
  useEffect(() => { setS(chatNotifySettings(chatId)); }, [chatId]);
  const set = (patch) => {
    const next = { ...s, ...patch };
    setS(next);
    saveChatNotifySettings(chatId, next);
  };
  const [perm, setPerm] = useState(() => (typeof Notification !== 'undefined' ? Notification.permission : 'denied'));

  return (
    <div className="ci-sec ci-notify">
      <div className="ci-sec-title">
        <BellRing width={15} height={15} /> Notifications
        <em>this browser only</em>
      </div>

      {perm !== 'granted' && (
        <button
          className="ci-perm"
          onClick={() => {
            requestNotifyPermission();
            setTimeout(() => setPerm(typeof Notification !== 'undefined' ? Notification.permission : 'denied'), 400);
          }}
        >
          {perm === 'denied'
            ? 'Desktop notifications are blocked for this site — allow them in your browser’s site settings.'
            : 'Turn on desktop notifications for Allo'}
        </button>
      )}

      <Row
        label={s.muted ? 'Muted' : 'Notify me'}
        sub={s.muted ? 'No sound or banners from this chat on this browser' : 'Sound and desktop banners are on'}
        on={!s.muted}
        onFlip={() => set({ muted: !s.muted })}
      />

      <div className={s.muted ? 'ci-dimmed' : ''}>
        <Row
          label="Show message preview"
          sub="Off shows only the sender's name in the desktop banner"
          on={s.preview}
          onFlip={() => set({ preview: !s.preview })}
        />

        <div className="ci-pickrow">
          <span className="ci-picklabel">Message tone</span>
          <div className="ci-chips">
            {MESSAGE_TONES.map((t) => (
              <button
                key={t.id} className={`ci-chip ${s.tone === t.id ? 'on' : ''}`}
                onClick={() => { set({ tone: t.id }); playMessageTone(t.id); }}
              >
                {s.tone === t.id && t.id !== 'none' ? <Play width={11} height={11} /> : null}{t.name}
              </button>
            ))}
          </div>
        </div>

        {/* 💬 …AND NOT FOR THE OFFICIAL ALLO ACCOUNT. Nobody there can place a
            call — the header drops the call buttons for this chat and the app
            does the same — so a ringtone picker for it is a setting that can
            never take effect. A control whose only honest description is "this
            does nothing" is worse than an absent one: it invites somebody to
            set it and then wonder why nothing rang. */}
        {/* 🔗 …AND NOT FOR A TEMPORARY GUEST CHAT EITHER. The other side is a
            browser holding a link, with no Allo account and no device to ring —
            the chat header and the info screen both drop the call buttons for it
            already. Same reasoning as the official account beside it: a ringtone
            for a call that can never arrive is a setting that cannot take
            effect. */}
        {/* 🟥 GROUPS USED TO BE EXCLUDED HERE, from when a group could not be
            called from a browser at all. They can now — so the person needs the
            same ringtone choice for them, and it is the setting the group ring
            actually reads (services/groupCalls.js). Leaving this hidden would
            have meant a group ringing with a tone nobody could change. */}
        {!official && !guest && (
          <>
            <Row
              label="Call alerts"
              sub="Ringtone and a banner when this contact calls this browser"
              on={s.callAlerts}
              onFlip={() => set({ callAlerts: !s.callAlerts })}
            />
            <div className={`ci-pickrow ${s.callAlerts ? '' : 'ci-dimmed'}`}>
              <span className="ci-picklabel">Ringtone</span>
              <div className="ci-chips">
                {RINGTONES.map((r) => (
                  <button
                    key={r.id} className={`ci-chip ${s.ringtone === r.id ? 'on' : ''}`}
                    onClick={() => { set({ ringtone: r.id }); previewRingtone(r.id); }}
                  >
                    {s.ringtone === r.id ? <Play width={11} height={11} /> : null}{r.name}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="ci-note">
        These settings live on this browser only — your phone’s notifications are untouched.
        Vibes notifications aren’t affected.
      </div>
    </div>
  );
}

/* ── encryption: the app's security code, verified state SYNCED ───────────────
   The 60 digits are computed exactly as the phone computes them, so they match
   by construction. What's new is where the *verification* lives: the chat doc,
   so verifying here shows on your phone AND tells the other person. */
function EncryptionSection({ chat, chatTitle, myUid, participants }) {
  const [code, setCode] = useState('');
  const [phase, setPhase] = useState('checking');   // checking | ready | error
  const [e2ee, setE2ee] = useState({});
  const [showCode, setShowCode] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [confirmAsk, setConfirmAsk] = useState(false);

  useEffect(() => watchE2ee(chat.id, (v) => setE2ee(v || {})), [chat.id]);

  useEffect(() => {
    let live = true;
    setPhase('checking'); setCode('');
    securityCodeFor(participants)
      .then((c) => { if (live) { setCode(c); setPhase('ready'); } })
      .catch(() => { if (live) setPhase('error'); });
    return () => { live = false; };
  }, [participants.join(','), chat.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const peerUids = participants.filter((u) => u !== myUid);
  const v = useMemo(() => verdictOf(e2ee, code, myUid, peerUids),
    [e2ee, code, myUid, peerUids.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // trust on first use — take the baseline once, and only when nothing is wrong
  useEffect(() => {
    if (phase !== 'ready' || !code) return;
    if (e2ee?.baseline?.[myUid]) return;
    setBaseline(chat.id, myUid, code).catch(() => {});
  }, [phase, code, e2ee, myUid, chat.id]);

  const state = phase === 'checking' ? 'checking' : phase === 'error' ? 'pending' : v.state;
  const title = {
    checking: 'Verifying…',
    pending: 'End-to-end encryption pending',
    ok: 'Messages are end-to-end encrypted',
    verified: 'Encryption verified',
    changed: 'Security code changed',
  }[state];
  const sub = {
    checking: 'Reading the keys that seal this conversation…',
    pending: 'Waiting for the keys that seal this conversation.',
    ok: `Only you and ${chat.isGroup ? 'the members of this group' : chatTitle} can read them — not even Allo.`,
    verified: 'You compared the code, so this conversation is confirmed.',
    changed: `${chat.isGroup ? 'A participant' : chatTitle} may have reinstalled Allo or added a device. Your messages are still encrypted — compare the code to confirm.`,
  }[state];

  return (
    <div className={`ci-sec ci-e2ee s-${state}`}>
      <div className="ci-sec-title">
        <ShieldLock width={15} height={15} /> Encryption
        <em>synced everywhere</em>
      </div>

      <div className="ci-e2ee-head">
        <span className={`ci-e2ee-badge ${state}`}>
          {state === 'changed' ? <Alert width={19} height={19} />
            : state === 'verified' ? <SecureCheck width={19} height={19} />
              : <Lock width={19} height={19} />}
        </span>
        <div>
          <b>{title}</b>
          <i>{sub}</i>
        </div>
      </div>

      {/* who has confirmed it — this is the part that crosses devices AND people */}
      {(v.mine || v.peers.length > 0) && (
        <div className="ci-e2ee-who">
          {v.mine && (
            <span className="ci-vtag me">
              <Check width={12} height={12} /> You verified{v.mine.via ? ` on ${v.mine.via === 'web' ? 'the web' : 'your phone'}` : ''} · {fullTimeLabel(v.mine.at)}
            </span>
          )}
          {v.peers.map((p) => (
            <span className="ci-vtag" key={p.uid}>
              <Check width={12} height={12} /> {chat.isGroup ? 'A member' : chatTitle} verified · {fullTimeLabel(p.v.at)}
            </span>
          ))}
        </div>
      )}

      {phase === 'ready' && (
        <>
          <div className="ci-e2ee-acts">
            <button className="ci-chip" onClick={() => setShowCode(!showCode)}>
              <Doc width={12} height={12} /> {showCode ? 'Hide' : 'Compare'} 60-digit code
            </button>
            <button className="ci-chip" onClick={() => setShowQR(!showQR)}>
              <SecureCheck width={12} height={12} /> {showQR ? 'Hide' : 'Show'} QR code
            </button>
          </div>

          {showQR && (
            <div className="ci-qr">
              <QRCodeSVG value={code} size={168} level="M" bgColor="#ffffff" fgColor="#0b1220" />
              <span>Open this chat's encryption screen on your phone and scan — a match verifies it for both of you.</span>
            </div>
          )}

          {showCode && (
            <div className="ci-code">
              <span className="ci-code-hint">Read these 60 digits with {chat.isGroup ? 'the group' : chatTitle}. If they match on both screens, no one is in the middle.</span>
              <code>{code}</code>
              <button
                className="ci-chip"
                onClick={() => { navigator.clipboard?.writeText(code).catch(() => {}); }}
              >
                Copy
              </button>
            </div>
          )}

          {!confirmAsk ? (
            <button
              className={`ci-verify ${state === 'verified' ? 'done' : ''}`}
              onClick={() => (state === 'verified'
                ? clearVerified(chat.id, myUid).catch(() => {})
                : setConfirmAsk(true))}
            >
              {state === 'verified' ? 'Remove my verification' : 'Mark as verified'}
            </button>
          ) : (
            <div className="ci-verify-ask">
              <span>Only do this once you have compared the code with {chat.isGroup ? 'a member' : chatTitle} in person or on a call.</span>
              <div>
                <button className="ci-chip on" onClick={() => { markVerified(chat.id, myUid, code, 'web').catch(() => {}); setConfirmAsk(false); }}>
                  It matches — verify
                </button>
                <button className="ci-chip" onClick={() => setConfirmAsk(false)}>Cancel</button>
              </div>
            </div>
          )}
        </>
      )}

      <div className="ci-note">
        The code is derived from every device key in this chat, so adding a device or
        reinstalling changes it. Verifying is shared: your phone and {chat.isGroup ? 'the group' : chatTitle} see it too.
      </div>
    </div>
  );
}

/* ── translate messages: the setting is SYNCED (chats/{id}.translation.{uid}) ──
   Works in groups as well as 1:1 — the field is keyed by MY uid, so it is my
   own setting for that conversation whoever else is in it. Note to Self is the
   one exclusion (translating your own notes to yourself isn't a thing).
   Flipping it here flips it on the phone: both read the same per-user field. */
function TranslateSection({ chatId, myUid, chatTitle, isGroup }) {
  const [tx, setTx] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showLangs, setShowLangs] = useState(false);
  useEffect(() => watchTranslation(chatId, myUid, setTx), [chatId, myUid]);

  const on = !!tx?.enabled;
  const from = tx?.from || 'Auto-Detect';
  const set = async (enabled, lang) => {
    setBusy(true);
    try { await saveTranslation(chatId, myUid, enabled, lang || from); } catch {}
    setBusy(false);
  };

  return (
    <div className="ci-sec ci-tx">
      <div className="ci-sec-title">
        <Translate width={15} height={15} /> Translate messages
        <em>synced everywhere</em>
      </div>

      <Row
        label={on ? `Translating to English` : 'Translate messages'}
        sub={on
          ? `New messages from ${isGroup ? 'other members' : chatTitle} are translated as they arrive`
          : 'Turn on to translate incoming messages into English'}
        on={on}
        onFlip={() => !busy && set(!on)}
      />

      <div className={`ci-pickrow ${on ? '' : 'ci-dimmed'}`}>
        <span className="ci-picklabel">{isGroup ? 'Group language' : 'Their language'}</span>
        <button className="ci-chip on" onClick={() => setShowLangs(!showLangs)}>{from}</button>
        {showLangs && (
          <div className="ci-chips" style={{ marginTop: 8 }}>
            {LANGUAGES.map((l) => (
              <button
                key={l} className={`ci-chip ${from === l ? 'on' : ''}`}
                onClick={() => { setShowLangs(false); set(on, l); }}
              >
                {l}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="ci-note">
        Only messages you receive after switching this on are translated, and each one
        keeps a badge to flip back to the original. Your phone follows this switch.
      </div>
    </div>
  );
}

/* ── read receipts: per-chat override over the global privacy setting ─────────
   Both fields are the app's own (chats/{id}.readReceiptsBy.{uid} and
   users/{uid}.privacySettings.readReceipts), so this switch and the phone's are
   the same switch. 1:1 and groups alike; Note to Self is excluded, as on the
   phone — there is no one to receipt. */
function ReceiptsSection({ chatId, myUid, isGroup }) {
  const [rr, setRr] = useState(OFF_UNKNOWN);
  const [busy, setBusy] = useState(false);
  useEffect(() => watchReadReceipts(chatId, myUid, setRr), [chatId, myUid]);

  const on = rr.ready && rr.enabled;
  const overridden = rr.perChat != null;
  const flip = async () => {
    if (busy || !rr.ready) return;
    setBusy(true);
    try { await setChatReadReceipts(chatId, myUid, !on); } catch {}
    setBusy(false);
  };
  const follow = async () => {
    setBusy(true);
    try { await clearChatReadReceipts(chatId, myUid); } catch {}
    setBusy(false);
  };

  return (
    <div className="ci-sec ci-rr">
      <div className="ci-sec-title">
        <CheckCheck width={15} height={15} /> Read receipts
        <em>synced everywhere</em>
      </div>

      <Row
        label={on ? 'Send read receipts' : 'Read receipts off'}
        sub={on
          ? (isGroup ? 'Members see when you have read their messages' : 'They see when you have read their messages')
          : (isGroup ? 'Members won’t see when you read their messages' : 'They won’t see when you read their messages')}
        on={on}
        onFlip={flip}
      />

      <div className="ci-rr-src">
        {overridden ? (
          <>
            <span>This chat overrides your global setting.</span>
            <button className="ci-chip" disabled={busy} onClick={follow}>
              Follow global ({rr.global ? 'on' : 'off'})
            </button>
          </>
        ) : (
          <span>Following your global setting ({rr.global ? 'on' : 'off'}). Changing it here sets it for this chat only.</span>
        )}
      </div>

      <div className="ci-note">
        Delivered ticks keep working either way — turning this off only stops the
        “read” tick. Your phone follows this switch.
      </div>
    </div>
  );
}

/* ── private media: cover this chat's photos and videos ───────────────────────
   Two homes for "on", and the difference is the whole point:
     synced  chats/{id}.security.{myUid}.privateMedia  — the app's own field
     local   this browser only, never leaves the machine
   Turning it on from the PHONE just arrives (nothing to ask). Turning it on
   HERE asks which of the two you meant — that question only makes sense in this
   direction, because only this direction has a choice. */
function PrivateMediaSection({ chat, myUid }) {
  const [, bump] = useState(0);
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => pmSubscribe(() => bump((n) => n + 1)), []);
  useEffect(() => watchPrivateMedia(chat.id, myUid), [chat.id, myUid]);

  const on = isPrivateMediaOn(chat.id);
  const synced = isSyncedOn(chat.id);
  const local = isLocalOn(chat.id);
  const revealed = isChatRevealed(chat.id);

  const turnOff = async () => {
    setBusy(true);
    pmSetLocal(chat.id, false);
    if (synced) { try { await pmSetSynced(chat.id, myUid, false); } catch {} }
    setBusy(false);
  };
  const turnOn = async (everywhere) => {
    setAsk(false); setBusy(true);
    if (everywhere) { try { await pmSetSynced(chat.id, myUid, true); } catch {} }
    else pmSetLocal(chat.id, true);
    setBusy(false);
  };

  return (
    <div className="ci-sec ci-pm">
      <div className="ci-sec-title">
        <Lock width={15} height={15} /> Private media
        <em>{synced ? 'on every device' : local ? 'this browser' : 'your choice'}</em>
      </div>

      <Row
        label={on ? 'Media is covered' : 'Cover photos and videos'}
        sub={on
          ? 'Photos, videos and albums stay covered until you unlock them one at a time'
          : 'Good for reading in public — nothing shows until you prove it’s you'}
        on={on}
        onFlip={() => { if (busy) return; if (on) turnOff(); else setAsk(true); }}
      />

      {ask && (
        <div className="ci-verify-ask">
          <span>Turn this on everywhere, or only in this browser?</span>
          <div>
            <button className="ci-chip on" disabled={busy} onClick={() => turnOn(true)}>
              Everywhere
            </button>
            <button className="ci-chip" disabled={busy} onClick={() => turnOn(false)}>
              This browser only
            </button>
            <button className="ci-chip" onClick={() => setAsk(false)}>Cancel</button>
          </div>
        </div>
      )}

      {on && (
        <div className="ci-rr-src">
          <span>
            {synced
              ? 'On for this chat on your phone too.'
              : 'On in this browser only — your phone is unaffected.'}
          </span>
          {!synced && (
            <button className="ci-chip" disabled={busy} onClick={() => turnOn(true)}>
              Also turn on everywhere
            </button>
          )}
        </div>
      )}

      {on && (
        <Row
          label="Reveal media"
          sub={revealed
            ? 'Everything in this chat is showing — flick back to cover it again'
            : 'Uncover this chat while you’re looking at it'}
          on={revealed}
          onFlip={() => setChatRevealed(chat.id, !revealed)}
        />
      )}

      <div className="ci-note">
        You can also click any covered item to reveal just that one. Everything re-covers
        the moment you leave this tab — there’s no timer to outlive the glance.
      </div>
    </div>
  );
}

/* ── last seen & online, toward THIS person ───────────────────────────────────
   The per-chat switches the app owns: chats/{id}.lastSeenBy.{myUid} and
   onlineBy.{myUid}. Absent = follow my global Privacy setting; an explicit
   true/false wins over it for this conversation only. Their client reads these
   exact fields, so flicking one here hides my dot on their phone AND their web.
   1:1 only — a group has no single "them", and Note to Self has no audience. */
function PresenceSection({ chat, myUid, chatTitle, myPrivacy }) {
  const [perChat, setPerChat] = useState({ ls: undefined, on: undefined });
  const [busy, setBusy] = useState('');

  useEffect(() => onSnapshot(fsDoc(db, 'chats', chat.id), (s) => {
    const d = s.data() || {};
    setPerChat({ ls: d.lastSeenBy?.[myUid], on: d.onlineBy?.[myUid] });
  }, () => {}), [chat.id, myUid]);

  // what my global setting says for a stranger — the baseline these override
  const globalLs = (myPrivacy?.lastSeen || 'everyone') !== 'nobody';
  const globalOn = (myPrivacy?.online || 'same_as_last_seen') === 'everyone' ? true : globalLs;

  const lsOn = perChat.ls != null ? perChat.ls !== false : globalLs;
  const onOn = perChat.on != null ? perChat.on !== false : globalOn;

  const flip = async (which, value) => {
    setBusy(which);
    try {
      if (which === 'ls') await setChatLastSeenShared(chat.id, myUid, value);
      else await setChatOnlineShared(chat.id, myUid, value);
    } catch {}
    setBusy('');
  };

  return (
    <div className="ci-sec ci-presence">
      <div className="ci-sec-title">
        <Eye width={15} height={15} /> Last seen &amp; online
        <em>synced everywhere</em>
      </div>

      <Row
        label="Share my last seen"
        sub={lsOn
          ? `${chatTitle} can see when you were last online`
          : `Hidden from ${chatTitle} — you won’t see theirs either`}
        on={lsOn}
        onFlip={() => busy !== 'ls' && flip('ls', !lsOn)}
      />
      <Row
        label="Share when I’m online"
        sub={onOn
          ? `Your green dot shows for ${chatTitle}`
          : `No green dot for ${chatTitle}, here or on their phone`}
        on={onOn}
        onFlip={() => busy !== 'on' && flip('on', !onOn)}
      />

      <div className="ci-rr-src">
        {(perChat.ls != null || perChat.on != null)
          ? <span>This chat overrides your global Privacy setting.</span>
          : <span>Following your global Privacy setting. Changing it here applies to {chatTitle} only.</span>}
      </div>

      <div className="ci-note">
        Turning either off hides it from this person everywhere — their phone and their
        web. To hide it from everyone at once, use Privacy in your profile.
      </div>
    </div>
  );
}

/* ── groups in common ─────────────────────────────────────────────────────────
   The app's own query (ChatInfoScreen.fetchCommonGroups): my groups, filtered
   to those where the other person is an ACTIVE participant — in `participants`
   and not in `leftParticipants` — and where I'm active too. A horizontal strip
   you can swipe or scroll through, each showing the group photo (or the member
   collage when it has none), the name, and who's in it. */
function CommonGroups({ myUid, otherUid, otherName, onOpen }) {
  const [groups, setGroups] = useState(null);   // null = loading

  useEffect(() => {
    let live = true;
    if (!myUid || !otherUid) { setGroups([]); return undefined; }
    setGroups(null);
    (async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'chats'),
          where('participants', 'array-contains', myUid),
          where('isGroup', '==', true),
        ));
        const out = [];
        snap.docs.forEach((d) => {
          const data = d.data();
          const left = data.leftParticipants || [];
          const themActive = (data.participants || []).includes(otherUid) && !left.includes(otherUid);
          const meActive = !left.includes(myUid);
          if (themActive && meActive) out.push({ id: d.id, ...data });
        });
        if (live) setGroups(out);
      } catch { if (live) setGroups([]); }
    })();
    return () => { live = false; };
  }, [myUid, otherUid]);

  /** "You & Abdullah", or "You & Abdullah and 3 others". */
  const whoLine = (g) => {
    const left = new Set(g.leftParticipants || []);
    const active = (g.participants || []).filter((u) => u && !left.has(u));
    const rest = Math.max(0, active.length - 2);
    const them = otherName || g.participantNames?.[otherUid] || 'them';
    if (rest <= 0) return `You & ${them}`;
    return `You & ${them} and ${rest} other${rest === 1 ? '' : 's'}`;
  };

  return (
    <div className="ci-sec ci-common">
      <div className="ci-sec-title">
        <GroupIcon width={15} height={15} /> Groups in common
        {groups?.length ? <em>{groups.length}</em> : null}
      </div>

      {groups === null ? (
        <div className="ci-empty">Looking…</div>
      ) : groups.length === 0 ? (
        <div className="ci-empty">No groups in common</div>
      ) : (
        <div className="ci-strip cg">
          {groups.map((g) => (
            <button className="cg-item" key={g.id} onClick={() => onOpen?.(g.id)} title={g.name || 'Group'}>
              <Avatar chat={g} myUid={myUid} />
              <b>{g.name || 'Group'}</b>
              <i>{whoLine(g)}</i>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ChatInfoPane({
  chat, chatTitle, subtitle, profile, myUid, myName, myProfile, myPrivacy, otherUid, msgs, mediaCtx, nameOf, onGroupCreated, allChats = [], blocking,
  onOpenMedia, onClose, width, onWidth, defaultW,
}) {
  /* ── 🖼 THE PROFILE PICTURE, LARGE ────────────────────────────────────────
     🟥 CLICKING IT DID NOTHING. Not on a person, not on Note to Self, not on
        the official account, not on a group. This is the screen somebody opens
        to look closer at who they are talking to, and the picture was the one
        thing on it that could not be looked at.
     🟢 A STRING DEPENDENCY, because the chat object is rebuilt on every
        snapshot — see the same note in ui/Avatar.jsx. */
  const [showAvatar, setShowAvatar] = useState(false);
  const [memberPhotos, setMemberPhotos] = useState({});
  const wantMembers = !!chat?.isGroup && !chat?.groupImage;
  const memberIdsKey = wantMembers ? activeMembers(chat).join(',') : '';
  useEffect(() => {
    if (!memberIdsKey) return undefined;
    let live = true;
    const ids = memberIdsKey.split(',').filter(Boolean);
    Promise.all(ids.map((uid) => getUserProfile(uid).catch(() => null))).then((ps) => {
      if (!live) return;
      const found = {};
      ps.forEach((prof, i) => {
        const u = prof?.profileImage || prof?.photoURL || null;
        found[ids[i]] = typeof u === 'string' && u ? u : null;
      });
      /* the same answer is not an update */
      setMemberPhotos((prev) => {
        const a = Object.keys(prev); const b = Object.keys(found);
        if (a.length === b.length && b.every((k) => prev[k] === found[k])) return prev;
        return found;
      });
    });
    return () => { live = false; };
  }, [memberIdsKey]);

  const avatarPageList = useMemo(() => avatarPages(
    chat?.isGroup ? chat : { ...(profile || {}), uid: otherUid, isGroup: false },
    {
      myUid,
      myPhoto: myProfile?.profileImage || null,
      myName: myProfile?.fullName || myName || '',
      fetched: memberPhotos,
    },
  ), [chat, profile, otherUid, myUid, myProfile?.profileImage, myProfile?.fullName, myName, memberPhotos]);
  /* 💬 see services/about — one visibility rule, shared with the chat header */
  const [infoAbout, setInfoAbout] = useState(null);
  const _aboutOk = !!otherUid && !chat?.isGroup && !isNoteToSelf(chat, myUid)
    && !isOfficialChat(chat) && otherUid !== 'allo-ai-bot' && otherUid !== myUid;
  useEffect(() => {
    if (!_aboutOk || !myUid) { setInfoAbout(null); return undefined; }
    let live = true;
    loadAboutFor(myUid, otherUid)
      .then((a) => { if (live) setInfoAbout(a); })
      .catch(() => { if (live) setInfoAbout(null); });
    return () => { live = false; };
  }, [_aboutOk, myUid, otherUid]);
  useEffect(() => {
    if (!infoAbout?.expiresAt) return undefined;
    const msLeft = infoAbout.expiresAt - Date.now();
    if (msLeft <= 0) { setInfoAbout(null); return undefined; }
    const tm = setTimeout(() => setInfoAbout(null), msLeft);
    return () => clearTimeout(tm);
  }, [infoAbout]);
  /* 🔗 a temporary chat has no Allo account on the other side — see the note at
     the group rows below */
  const guestChat = !!chat?.isGuest;
  /* 💬 the official Allo account — see ChatWindow for why this is one flag */
  const officialChat = isOfficialChat(chat);
  const [tab, setTab] = useState('media');
  const [makeGroup, setMakeGroup] = useState(false);
  const [addToList, setAddToList] = useState(false);
  const [doExport, setDoExport] = useState(false);
  const [doClear, setDoClear] = useState(false);
  const [askLeave, setAskLeave] = useState(false);
  const [askBlock, setAskBlock] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [actErr, setActErr] = useState('');
  const [docView, setDocView] = useState(null);       // message id in the doc viewer
  const pendingOpen = useRef(null);                    // {id, index} waiting on decrypt

  /* 🟢 THE MEDIA-CLEAR WATERMARK, AS A NUMBER. The obvious dependency here is
     `chat`, but that object is rebuilt on every chat-doc snapshot — and those
     arrive on every message, receipt and watermark write — so the whole media
     strip would recollect constantly. The millisecond value changes only when a
     clear actually happens, which is exactly when these lists should rebuild
     (including a clear made on the phone, which is the case the stale dependency
     array was silently missing). */
  /* 🟢 AM I STILL IN THIS GROUP? `leftParticipants` is the answer, NOT
     `participants` — leaving keeps me in the latter on purpose (every chat query
     is array-contains on it), so reading participants here would say "still a
     member" forever. Covers a leave made on the phone too, since it's the same
     field. */
  const amGone = !!chat.isGroup && (chat.leftParticipants || []).includes(myUid);

  /* Block state, both directions, from the one listener App already runs on my
     own user doc — the phone's model exactly. */
  const iBlockedThem = !!(blocking && otherUid && blocking.byMe?.has(otherUid)) && !chat.isGroup;
  const theyBlockedMe = !!(blocking && otherUid && blocking.me?.has(otherUid)) && !chat.isGroup;

  const mediaClearAt = mediaClearedMs(chat, myUid);
  const mediaGone = useCallback(
    (m) => !!mediaClearAt && !!kindOf(m) && msOf(m) <= mediaClearAt,
    [mediaClearAt],
  );

  /* ── collect, newest first ── */
  const mediaTiles = useMemo(() => {
    const out = [];
    for (const m of msgs) {
      if (m.isDeleted || m.deleted) continue;
      if (mediaGone(m)) continue;                     // cleared media isn't listed
      if (m.type === 'image') out.push({ m, index: 0, kind: 'image', thumb: dataUri(m.imageThumbnailBase64), url: m._media });
      else if (m.type === 'video') out.push({ m, index: 0, kind: 'video', thumb: dataUri(m.videoThumbnailBase64), url: m._media });
      else if (m.type === 'images') {
        const n = (m.images || []).length || (m._mediaBatch || []).length;
        /* 🧩 only the album's items still part of it (and not cleared from storage) */
        const vis = new Set(albumVisibleIndices({ ...m, images: Array.from({ length: n }) }, myUid));
        for (let i = 0; i < n; i += 1) {
          if (!vis.has(i) || (m.deletedImageIndices || []).includes(i)) continue;
          const it = m._mediaBatch?.[i];
          out.push({ m, index: i, kind: it?.kind || 'image', thumb: dataUri(m.batchThumbnails?.[i]), url: it?.url });
        }
      }
    }
    return out.sort((a, b) => msOf(b.m) - msOf(a.m));
  }, [msgs, mediaGone, myUid]);

  const linkCards = useMemo(() => {
    const out = [];
    for (const m of msgs) {
      if (m.isDeleted || m.deleted || !m.linkMetadata) continue;
      const meta = normalizeLink(m.linkMetadata);
      if (!meta?.url) continue;
      let domain = '';
      try { domain = new URL(meta.url).hostname.replace(/^www\./, ''); } catch { domain = meta.url; }
      out.push({ m, meta, domain });
    }
    return out.sort((a, b) => msOf(b.m) - msOf(a.m));
  }, [msgs]);

  const docRows = useMemo(
    () => msgs.filter((m) => m.type === 'document' && !m.isDeleted && !m.deleted && !mediaGone(m))
      .sort((a, b) => msOf(b) - msOf(a)),
    [msgs, mediaGone],
  );

  /* ── open media: decrypt on demand, auto-open when the bytes land ── */
  const clickTile = (t) => {
    if (t.url) { onOpenMedia(t.m, t.index); return; }
    if (t.m._deviceOnly) return;
    pendingOpen.current = { id: t.m.id, index: t.index };
    if (t.m.type === 'images') mediaCtx?.loadTile(t.m, t.index);
    else mediaCtx?.requestMedia(t.m.id, true);
  };
  useEffect(() => {
    const p = pendingOpen.current;
    if (!p) return;
    const m = msgs.find((x) => x.id === p.id);
    if (!m) { pendingOpen.current = null; return; }
    const url = m.type === 'images' ? m._mediaBatch?.[p.index]?.url : m._media;
    if (url) { pendingOpen.current = null; onOpenMedia(m, p.index); }
    else if (m._mediaResolved && !m._loading && !url) pendingOpen.current = null; // gave up
  }, [msgs, onOpenMedia]);

  const openDoc = (m) => {
    setDocView(m.id);
    if (!m._media && !m._deviceOnly) mediaCtx?.requestMedia(m.id, true);
  };
  const docLive = docView ? (msgs.find((x) => x.id === docView) || null) : null;

  /* ── resize: wider yes, narrower than the default never ── */
  const startDrag = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    const maxW = Math.min(820, Math.round(window.innerWidth * 0.55));
    const move = (ev) => onWidth(Math.max(defaultW, Math.min(maxW, startW + (startX - ev.clientX))));
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.cursor = '';
    };
    document.body.style.cursor = 'col-resize';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const counts = { media: mediaTiles.length, links: linkCards.length, docs: docRows.length };

  /* ── the collapsing header, like the app's ChatInfoScreen ──────────────────
     Scroll drives a single 0→1 progress var on the pane root; CSS does the
     rest (hero avatar shrinks + fades, the "Contact info" pill hands its spot
     to a small centred avatar + name). Writing a CSS var directly on the DOM
     keeps this off React's render path — 60fps for free. */
  const rootRef = useRef(null);
  const rafRef = useRef(0);
  const onBodyScroll = (e) => {
    const el = e.currentTarget;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const p = Math.max(0, Math.min(1, el.scrollTop / 150));
      rootRef.current?.style.setProperty('--ci-p', String(p));
    });
  };

  return (
    <div className="chat-tools chat-info" ref={rootRef}>
      {/* drag edge — double-click snaps back to the original width */}
      <div
        className="ci-grip" title="Drag to widen · double-click to reset"
        onPointerDown={startDrag} onDoubleClick={() => onWidth(defaultW)}
      />

      <div className="ct-head ci-head">
        <div className="ct-tabs ci-pill"><button className="on">{chat.isGroup ? 'Group info' : 'Contact info'}</button></div>
        {/* fades in as you scroll — the pic + the name only, dead centre */}
        {/* 🟥 THE BADGE HAS TO SURVIVE THE COLLAPSE. Scrolling replaces the hero
            with this compact copy, and the official mark was only on the hero —
            so the one line still naming the account lost the only thing that
            distinguishes the real Allo from an impersonation. A verification
            mark that disappears when the header shrinks verifies nothing. */}
        <div className="ci-mini" aria-hidden>
          <Avatar profile={profile} chat={chat} peerUid={otherUid} size="sm" myUid={myUid} />
          <b>
            {chatTitle}
            {isOfficialChat(chat) && (
              <span className="official-badge" title="Official Allo account">
                <Verified width={13} height={13} />
              </span>
            )}
            {/* ✔️ Note to Self wears the mark here too — as it does in the list and the header */}
            {isNoteToSelf(chat, myUid) && <Verified width={13} height={13} />}
            {!chat.isGroup && otherUid ? <SupporterBadge uid={otherUid} size={13} /> : null}
            {/* 🔗 THE TEMPORARY MARK SURVIVES THE COLLAPSE TOO, for the same
                reason the verification mark does: once you have scrolled, this
                one line is the whole header, and "this conversation deletes
                itself" is not a detail to drop at that point. The chat list and
                the chat header both carry this chip already; it was only ever
                missing here. */}
            {guestChat && (
              <span className={`temp-chip ${guestChatExpired(chat) ? 'dead' : ''}`}
                title={guestChatExpired(chat) ? 'This temporary chat has ended' : 'Temporary chat'}>
                <Timer width={11} height={11} />
              </span>
            )}
          </b>
        </div>
        <button className="icon-btn" onClick={onClose} aria-label="Close"><X /></button>
      </div>

      <div className="ci-body" onScroll={onBodyScroll}>
        {/* ── the hero: picture top-centre, name beneath — shrinks as you scroll ── */}
        <div className="ci-hero">
          {/* 🟢 A BUTTON, NOT A DIV WITH AN onClick. It is the one interactive
              thing in the hero, and a keyboard reaches it for free. */}
          <button
            type="button"
            className="ci-hero-ava"
            onClick={() => avatarPageList.length && setShowAvatar(true)}
            aria-label="View profile picture"
          >
            <Avatar profile={profile} chat={chat} peerUid={otherUid} size="lg" myUid={myUid} />
          </button>
          <b className="ci-name">
            {chatTitle}
            {/* 💬 the same mark the chat list and the header carry — this is the
                screen someone opens BECAUSE they are checking who they are
                talking to, so it is the last place the badge should be absent */}
            {isOfficialChat(chat) && (
              <span className="official-badge" title="Official Allo account">
                <Verified width={16} height={16} />
              </span>
            )}
            {isNoteToSelf(chat, myUid) && <Verified width={16} height={16} />}
            {!chat.isGroup && otherUid ? <SupporterBadge uid={otherUid} size={16} /> : null}
            {/* 🔗 …and the same chip on the hero. This is the screen someone
                opens to find out what this conversation IS, so the one fact that
                separates it from every other chat belongs on it. */}
            {guestChat && (
              <span className={`temp-chip ${guestChatExpired(chat) ? 'dead' : ''}`}
                title={guestChatExpired(chat) ? 'This temporary chat has ended' : 'Temporary chat'}>
                <Timer width={13} height={13} />
              </span>
            )}
          </b>
          {subtitle ? <i className="ci-sub">{subtitle}</i> : null}
          {/* 💬 their About status, under the picture — the same speech bubble the
              chat header shows, so the two surfaces read as one thing in two
              places. Ordinary people only: a group has no status, Note to Self is
              you, and the official account and Allo AI are not people. */}
          {!!infoAbout && <div className="ci-about">{infoAbout.text}</div>}
        </div>

        {/* ── media / links / docs, one scrollable strip ── */}
        <div className="ci-sec">
          <div className="ci-tabs">
            {['media', 'links', 'docs'].map((t) => (
              <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
                {t === 'media' ? 'Media' : t === 'links' ? 'Links' : 'Docs'}
                {counts[t] > 0 && <em>{counts[t]}</em>}
              </button>
            ))}
          </div>

          {tab === 'media' && (
            mediaTiles.length === 0
              ? <div className="ci-empty">No photos or videos yet</div>
              : (
                <div className="ci-strip">
                  {mediaTiles.map((t, i) => (
                    <button
                      className="ci-tile" key={`${t.m.id}-${t.index}`}
                      onClick={() => (isPrivateMediaOn(chat.id) ? null : clickTile(t))}
                      title={isPrivateMediaOn(chat.id) ? 'Covered — reveal it in the chat' : nameOf(t.m)}
                    >
                      {t.url
                        ? (t.kind === 'video'
                          // eslint-disable-next-line jsx-a11y/media-has-caption
                          ? <video src={t.url} muted preload="metadata" />
                          : <img src={t.url} alt="" loading="lazy" />)
                        : t.thumb
                          ? <img src={t.thumb} alt="" loading="lazy" />
                          : <span className="ci-tile-blank"><Doc width={16} height={16} /></span>}
                      {t.kind === 'video' && !isPrivateMediaOn(chat.id) && <span className="ci-tile-play"><Play width={14} height={14} /></span>}
                      {/* 🔒 a read-only surface, like the phone's Starred / message-info:
                          it stays covered and does not offer an unlock */}
                      {isPrivateMediaOn(chat.id) && <span className="ci-tile-lock"><Lock width={14} height={14} /></span>}
                    </button>
                  ))}
                </div>
              )
          )}

          {tab === 'links' && (
            linkCards.length === 0
              ? <div className="ci-empty">No links yet</div>
              : (
                <div className="ci-strip links">
                  {linkCards.map((l) => (
                    <a className="ci-link" key={l.m.id} href={l.meta.url} target="_blank" rel="noreferrer" title={l.meta.url}>
                      {l.meta.images?.[0]
                        ? <img className="ci-link-img" src={l.meta.images[0]} alt="" loading="lazy" />
                        : <span className="ci-link-ico"><LinkIcon width={16} height={16} /></span>}
                      <b>{l.meta.title || l.domain}</b>
                      <i>{l.domain}</i>
                    </a>
                  ))}
                </div>
              )
          )}

          {tab === 'docs' && (
            docRows.length === 0
              ? <div className="ci-empty">No documents yet</div>
              : (
                <div className="ci-strip docs">
                  {docRows.map((m) => {
                    const name = m.documentName || 'Document';
                    const { ext, color } = docStyle(name);
                    return (
                      <button className="ci-doc" key={m.id} onClick={() => openDoc(m)} title={name}>
                        <span className="doc-tile" style={{ background: color }}>
                          <Doc width={16} height={16} /><span className="doc-ext">{ext || 'FILE'}</span>
                        </span>
                        <b>{name}</b>
                        <i>{[ext, humanBytes(m.documentSize)].filter(Boolean).join(' · ')}</i>
                      </button>
                    );
                  })}
                </div>
              )
          )}
        </div>

        {/* 🟢 JOIN A CALL THAT IS ALREADY HAPPENING — the same green row the
            phone's ChatInfoScreen puts here, driven by the same `activeCall`
            field, so a call started on a phone can be picked up in the browser
            and the other way round. */}
        {chat.isGroup && chat.activeCall?.participants?.length > 0 && (
          <button className="ci-sec ci-join" onClick={() => joinGroupCall(chat)}>
            <span className="ci-action-ico">
              {chat.activeCall.callType === 'voice'
                ? <Phone width={17} height={17} />
                : <Video width={17} height={17} />}
            </span>
            <span>
              <b>Join group call</b>
              <i>{chat.activeCall.participants.length} in the call</i>
            </span>
          </button>
        )}

        <NotifySection chatId={chat.id} isGroup={!!chat.isGroup} official={officialChat} guest={guestChat} />

        {/* 💬 NOT FOR THE OFFICIAL ALLO ACCOUNT. This thread is deliberately
            NOT end-to-end encrypted — the chat document carries `e2ee: false` and
            every client reads it — because a support team has to be able to read
            what it is answering. Showing an encryption panel over a chat that has
            none would be the one kind of lie this panel exists to prevent, and a
            safety-number comparison with a shared account is meaningless besides. */}
        {!officialChat && (
          <EncryptionSection
            chat={chat} chatTitle={chatTitle} myUid={myUid}
            participants={(chat.participants || []).filter(Boolean)}
          />
        )}

        {chat.isGroup && <GroupSettings chat={chat} myUid={myUid} myName={myName} />}

        {/* groups translate too — the setting is per-USER, so it's mine either way.
            Note to Self is the only exclusion.
            💬 …and the official Allo account, which already speaks the language
            the person set: every reply is written by the team in their language
            rather than machine-translated into it. Offering to translate our own
            support replies would be offering to make them worse. */}
        {!isNoteToSelf(chat, myUid) && !officialChat && (
          <TranslateSection
            chatId={chat.id} myUid={myUid} chatTitle={chatTitle} isGroup={!!chat.isGroup}
          />
        )}

        {/* 💬 …and not for the official account, which has no reader to send a
            receipt to. Its messages settle on two grey ticks by design — see
            deriveReceiptStatus — so a switch controlling whether to tell it when
            you have read something would govern nothing. */}
        {!isNoteToSelf(chat, myUid) && !officialChat && (
          <ReceiptsSection chatId={chat.id} myUid={myUid} isGroup={!!chat.isGroup} />
        )}

        {/* all chat types — including Note to Self, which has media worth covering */}
        <PrivateMediaSection chat={chat} myUid={myUid} />

        {/* 1:1 only — a group has no single "them", Note to Self no audience */}
        {/* 💬 …and never for the official account. "Share my last seen with
            Allo" and "share when I'm online" offer a reciprocity that does not
            exist: there is no person on the other side whose presence you could
            ever see in return, and the header shows none. */}
        {!chat.isGroup && !isNoteToSelf(chat, myUid) && !officialChat && (
          <PresenceSection chat={chat} myUid={myUid} chatTitle={chatTitle} myPrivacy={myPrivacy} />
        )}

        {/* 🟢 "Create group with <them>" — the app's own ChatInfo entry point,
            with this person pre-selected in the sheet */}
        {/* 🔗 …AND NEVER FOR A GUEST. There is no Allo account on the other side to
            add to a group: a guest is a browser holding a link, they own no
            groups so "groups in common" is always empty, and a group built
            around them would outlive the link that created them and be left with
            a member nobody can reach. Both rows are absent rather than
            present-and-failing — the same choice the header, the call buttons
            and the chat-info screen already make for this chat type. */}
        {!chat.isGroup && !guestChat && !officialChat && !isNoteToSelf(chat, myUid) && otherUid && (
          <button className="ci-sec ci-action" onClick={() => setMakeGroup(true)}>
            <span className="ci-action-ico"><GroupIcon width={17} height={17} /></span>
            <span>
              <b>Create group with {chatTitle}</b>
              <i>Add more people and start a new group</i>
            </span>
          </button>
        )}

        {!chat.isGroup && !guestChat && !officialChat && !isNoteToSelf(chat, myUid) && otherUid && (
          <CommonGroups
            myUid={myUid} otherUid={otherUid} otherName={chatTitle}
            onOpen={(id) => onGroupCreated?.(id)}
          />
        )}

        {/* 🟢 every chat kind can be filed — groups and Note to Self included */}
        <button className="ci-sec ci-action" onClick={() => setAddToList(true)}>
          <span className="ci-action-ico"><ListIcon width={17} height={17} /></span>
          <span>
            <b>Add to a list</b>
            <i>File this chat under Family, Work, anything you like</i>
          </span>
        </button>

        <button className="ci-sec ci-action" onClick={() => setDoExport(true)}>
          <span className="ci-action-ico"><Download width={17} height={17} /></span>
          <span>
            <b>Export chat</b>
            <i>Download a readable copy, and see past exports</i>
          </span>
        </button>

        <button className="ci-sec ci-action danger" onClick={() => setDoClear(true)}>
          <span className="ci-action-ico"><Trash width={17} height={17} /></span>
          <span>
            <b>Clear chat</b>
            <i>Remove the media, or empty the conversation</i>
          </span>
        </button>

        {/* 🟢 LEAVE GROUP — groups only, and only while I'm still in one.
            Deliberately NOT the chat list's "Delete & exit": this steps out of
            the conversation and leaves the chat and its history sitting in my
            list. Hidden once I've left (from here or from the phone). */}
        {chat.isGroup && !amGone && (
          askLeave ? (
            <div className="ci-sec ci-verify-ask ci-danger-ask">
              <span>
                Leave <b>{chatTitle}</b>? You&rsquo;ll stop receiving its messages and
                everyone will see that you left. The chat stays in your list, and an
                admin can add you back.
              </span>
              <div>
                <button
                  className="ci-chip danger" disabled={leaving}
                  onClick={async () => {
                    setLeaving(true); setActErr('');
                    try {
                      await leaveGroup(chat, { uid: myUid, name: myName });
                      setAskLeave(false);
                      onClose?.();
                    } catch { setActErr('That didn’t go through — try again.'); }
                    setLeaving(false);
                  }}
                >
                  <Logout width={12} height={12} /> {leaving ? 'Leaving…' : 'Leave group'}
                </button>
                <button className="ci-chip" onClick={() => setAskLeave(false)}>Cancel</button>
              </div>
              {actErr && <div className="pm-err">{actErr}</div>}
            </div>
          ) : (
            <button className="ci-sec ci-action danger" onClick={() => { setActErr(''); setAskLeave(true); }}>
              <span className="ci-action-ico"><Logout width={17} height={17} /></span>
              <span>
                <b>Leave group</b>
                <i>Step out of the conversation — the chat stays in your list</i>
              </span>
            </button>
          )
        )}

        {/* 🟢 BLOCK — 1:1 only, never Note to Self. The same two mirrored writes
            the phone makes, so the block lands on every device of mine and
            reaches them through their own user doc.
            💬 …AND NEVER FOR THE OFFICIAL ALLO ACCOUNT. Blocking works by writing
            to the other party's user document and refusing their messages; here
            that party is the product itself, and the announcements it sends are
            the channel through which somebody would be told their account is in
            trouble. Offering a button that appears to switch that off — and then
            does not, because the broadcast path is not the 1:1 message path — is
            a promise the product cannot keep. Muting it is what this screen
            offers instead, and that one is real. */}
        {!chat.isGroup && !isNoteToSelf(chat, myUid) && !officialChat && otherUid && (
          askBlock ? (
            <div className="ci-sec ci-verify-ask ci-danger-ask">
              <span>
                {iBlockedThem
                  ? `Unblock ${chatTitle}? You’ll be able to message and call each other again, on every device.`
                  : `Block ${chatTitle}? They won’t be able to message or call you, and you won’t be able to message them. This applies on your phone too.`}
              </span>
              <div>
                <button
                  className={`ci-chip ${iBlockedThem ? '' : 'danger'}`}
                  onClick={async () => {
                    setActErr('');
                    try {
                      if (iBlockedThem) await unblockUser(myUid, otherUid);
                      else await blockUser(myUid, otherUid);
                      setAskBlock(false);
                    } catch { setActErr('That didn’t go through — try again.'); }
                  }}
                >
                  <Slash width={12} height={12} /> {iBlockedThem ? 'Unblock' : 'Block'}
                </button>
                <button className="ci-chip" onClick={() => setAskBlock(false)}>Cancel</button>
              </div>
              {actErr && <div className="pm-err">{actErr}</div>}
            </div>
          ) : (
            <button
              className={`ci-sec ci-action ${iBlockedThem ? '' : 'danger'}`}
              onClick={() => { setActErr(''); setAskBlock(true); }}
            >
              <span className="ci-action-ico"><Slash width={17} height={17} /></span>
              <span>
                <b>{iBlockedThem ? `Unblock ${chatTitle}` : `Block ${chatTitle}`}</b>
                <i>
                  {iBlockedThem
                    ? 'Let them message and call you again'
                    : (theyBlockedMe
                      ? 'They have blocked you — blocking back stops them entirely'
                      : 'Stop their messages and calls, everywhere')}
                </i>
              </span>
            </button>
          )
        )}
      </div>

      {doExport && (
        <ExportChatModal
          chat={chat} chatTitle={chatTitle} myUid={myUid} msgs={msgs} nameOf={nameOf}
          onClose={() => setDoExport(false)}
        />
      )}
      {doClear && (
        <ClearChatSheet
          chat={chat} chatTitle={chatTitle} myUid={myUid} msgs={msgs}
          onClose={() => setDoClear(false)}
          onCleared={() => onClose?.()}
        />
      )}

      {addToList && (
        <AddToListModal
          myUid={myUid} chats={allChats} chatId={chat.id} chatTitle={chatTitle}
          onClose={() => setAddToList(false)}
        />
      )}

      {makeGroup && (
        <NewGroupModal
          me={{ uid: myUid, name: myName || 'Me', image: myProfile?.profileImage || '' }}
          initialMembers={[{
            uid: otherUid,
            fullName: profile?.fullName || chatTitle,
            username: profile?.username || '',
            profileImage: profile?.profileImage || '',
          }]}
          onClose={() => setMakeGroup(false)}
          onCreated={(gid, invited) => onGroupCreated?.(gid, invited)}
        />
      )}

      {docLive && (
        <DocViewer
          m={docLive}
          senderName={nameOf(docLive)}
          mine={docLive.senderId === myUid}
          onClose={() => setDocView(null)}
        />
      )}

      {/* 🖼 Deliberately NOT MediaViewer, which has a header bar, a sender pill,
          a download control and a filmstrip. None of them has anything to say
          about a profile picture. */}
      {showAvatar && (
        <AvatarViewer pages={avatarPageList} onClose={() => setShowAvatar(false)} />
      )}
    </div>
  );
}
